import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Paper,
} from "@material-ui/core";
import FindInPage from "@material-ui/icons/FindInPage";
import ClearIcon from '@material-ui/icons/Clear';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
  return order === "desc"
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}

const headRows = [
  {
    id: "NomeFantasia",
    numeric: false,
    disablePadding: false,
    label: "Nome Fantasia",
    align: "left"
  },
  {
    id: "Inscricao",
    numeric: true,
    disablePadding: false,
    label: "CNPJ",
    align: "center"
  },
  {
    id: "Sigla",
    numeric: false,
    disablePadding: false,
    label: "Sigla",
    align: "left"
  },
  {
    id: "ExigePreCadastro",
    numeric: true,
    disablePadding: false,
    label: "Exige Pré-Cadastro",
    align: "center"
  },
  {
    id: "PossuiCheckoutInterno",
    numeric: true,
    disablePadding: false,
    label: "Checkout Interno",
    align: "center"
  },
  {
    id: "PossuiCheckoutExterno",
    numeric: true,
    disablePadding: false,
    label: "Checkout Externo",
    align: "center"
  },
  {
    id: "Ativo",
    numeric: true,
    disablePadding: false,
    label: "Status",
    align: "center"
  },
  {
    id: "Logotipo",
    numeric: true,
    disablePadding: false,
    label: "Logotipo",
    align: "center"
  },
  {
    id: "action",
    numeric: true,
    disablePadding: true,
    label: "",
    align: "center"
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headRows.map((row) => (
          <TableCell
            className="table-cell-style"
            key={row.id}
            align={row.align}
            padding={row.disablePadding ? "none" : "default"}
            sortDirection={orderBy === row.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === row.id}
              direction={order}
              onClick={createSortHandler(row.id)}
            >
              {row.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: "auto",
  },
  editIcon: {
    cursor: "pointer",
  },
}));

export default function EnhancedTable(props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("cnpj");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [acquirerList, setAcquirerList] = React.useState([]);

  useEffect(() => {
    setAcquirerList(
      props.tableFilter.length > 0 ? props.tableFilter : props.tableData
    );
    setPage(0);
  }, [props.tableData, props.tableFilter]);

  function handleRequestSort(event, property) {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  }

  function handleClick(sigla) {
    let acquirer = acquirerList.filter((x) => x.Sigla === sigla)[0];
    props.navigateToEdit(acquirer);
  }

  function handleChangePage(event, newPage) {
    setPage(newPage);
  }

  function handleChangeRowsPerPage(event) {
    setRowsPerPage(+event.target.value);
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <div className={classes.tableWrapper}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            stickyHeader 
            size={"medium"}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={
                props.tableFilter.length <= 0
                  ? acquirerList.length
                  : props.tableFilter.length
              }
            />
            <TableBody>
              {stableSort(
                props.tableFilter.length > 0 ? props.tableFilter : acquirerList,
                getSorting(order, orderBy)
              )
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row.cnpj}
                    >
                      <TableCell align="left">{row.NomeFantasia}</TableCell>
                      <TableCell align="center">{row.Inscricao}</TableCell>
                      <TableCell align="left">{row.Sigla}</TableCell>
                      <TableCell align="center">
                        {row.ExigePreCadastro ? "Sim" : "Não"}
                      </TableCell>
                      <TableCell align="center">
                        {row.PossuiCheckoutInterno ? "Sim" : "Não"}
                      </TableCell>
                      <TableCell align="center">
                        {row.PossuiCheckoutExterno ? "Sim" : "Não"}
                      </TableCell>
                      <TableCell align="center">
                        {row.Ativo ? "Ativo" : "Inativo"}
                      </TableCell>
                      <TableCell align="center">
                        {row.Logotipo !== null ?
                        <CheckRoundedIcon style={{color:"green"}} /> :
                        <ClearIcon color="disabled" />}
                      </TableCell>
                      <TableCell align="center">
                        <FindInPage
                          onClick={() => handleClick(row.Sigla)}
                          className={classes.editIcon}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={
            props.tableFilter.length <= 0
              ? acquirerList.length
              : props.tableFilter.length
          }
          rowsPerPage={rowsPerPage}
          page={page}
          labelRowsPerPage={"Linhas por página:"}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to === -1 ? count : to} de ${count}`
          }
          backIconButtonProps={{
            "aria-label": "Anterior",
          }}
          nextIconButtonProps={{
            "aria-label": "Próximo",
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}
